import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { AuthorizationModel } from '../models/data/authorization.model';
import { LocalStorageKeyConstants } from '../constants/constants';
import { UserSessionService } from './user-session.service';
import { AuthorizationStateService } from './authorization/authorization-state.service';
import { AuthorizationService } from './authorization/authorization.service';

@Injectable({ providedIn: 'root' })
export class DismissibleNotesService {
  private userSessionService = inject(UserSessionService);
  private authorizationStateService = inject(AuthorizationStateService);
  private authorizationService = inject(AuthorizationService);

  // Scope string -> set of dismissed note keys. The active-org scope entry is reseeded by the
  // effect below on every userId/authorization/activeOrganization change — switching orgs on an
  // org-scoped persona swaps that entry in without a reload. Explicit-id scopes (profile pages
  // viewing a non-active org) are populated lazily on first read and cached for the session.
  private dismissedByScope = signal<Map<string, Set<string>>>(new Map());

  constructor() {
    effect(() => {
      const userId = this.userSessionService.activeUser()?.userId;
      const auth = this.authorizationStateService.authorizationState();
      const orgId = this.userSessionService.activeOrganization()?.organizationId;
      untracked(() => {
        const scope = this.computeScope(auth, orgId);
        const next = new Map(this.dismissedByScope());
        if (userId && scope) {
          // Overwrite only the active-org entry — any explicit-id entries loaded for profile
          // pages stay cached, keyed by their own ids, and remain correct across org switches.
          next.set(scope, this.loadDismissedKeys(userId, scope));
        } else {
          // Signed out or scope unresolvable — drop everything so a different user signing in
          // under the same session starts clean.
          next.clear();
        }
        this.dismissedByScope.set(next);
      });
    });
  }

  // organizationId forces per-organization scope for pages that render for an organization other
  // than the one that is active (profile pages — see profile-organization-scope.service.ts). Staff
  // personas still collapse to 'global' regardless, so one dismissal continues to follow them.
  // Omit it for anything that renders against the active organization (forms, the default admin
  // area, etc.) — the service falls back to activeOrganization in that case.
  public isDismissed(noteKey: string, organizationId?: number): boolean {
    const scope = this.resolveScope(organizationId);
    if (!scope) return false;
    const cached = this.dismissedByScope().get(scope);
    if (cached) return cached.has(noteKey);
    // First read for an explicit-id scope — hydrate from storage, cache, then answer. Without
    // caching here every change detection pass would re-scan localStorage for that scope.
    const userId = this.userSessionService.activeUser()?.userId;
    if (!userId) return false;
    const loaded = this.loadDismissedKeys(userId, scope);
    this.dismissedByScope.update(prev => new Map(prev).set(scope, loaded));
    return loaded.has(noteKey);
  }

  public dismiss(noteKey: string, organizationId?: number): void {
    const userId = this.userSessionService.activeUser()?.userId;
    const scope = this.resolveScope(organizationId);
    // No signed-in user or no resolvable scope (e.g. org persona before an org is picked) means
    // there is nowhere to persist the dismissal. The note stays visible; next click will try again.
    if (!userId || !scope) return;

    const storageKey = LocalStorageKeyConstants.DISMISSED_NOTE(userId, scope, noteKey);
    // Persistence is best-effort — a full quota, Safari Private Mode, or a browser with storage
    // disabled all throw from setItem. The in-memory signal update still runs so the note hides
    // for the rest of this session; the dismissal just doesn't survive a reload.
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // swallow — see comment above
    }
    this.dismissedByScope.update(prev => {
      const next = new Map(prev);
      const existing = next.get(scope) ?? new Set<string>();
      next.set(scope, new Set(existing).add(noteKey));
      return next;
    });
  }

  // Explicit organizationId wins so profile-page callers always resolve against the organization
  // on screen. Staff always collapse to 'global'. Otherwise fall back to the active organization.
  private resolveScope(organizationId: number | undefined): string | undefined {
    const auth = this.authorizationStateService.authorizationState();
    if (!auth) return undefined;
    if (this.authorizationService.isStaff(auth)) return 'global';
    if (organizationId !== undefined) return `org_${organizationId}`;
    const activeOrgId = this.userSessionService.activeOrganization()?.organizationId;
    return activeOrgId !== undefined ? `org_${activeOrgId}` : undefined;
  }

  // 'global' for staff personas — one dismissal follows them across every org. 'org_{orgId}' for
  // org-scoped members — dismissal is per member, per organization, matching the ticket's AC.
  // Used by the reseeding effect to compute the active-org scope.
  private computeScope(auth: AuthorizationModel | undefined, orgId: number | undefined): string | undefined {
    if (!auth) return undefined;
    if (this.authorizationService.isStaff(auth)) return 'global';
    return orgId !== undefined ? `org_${orgId}` : undefined;
  }

  private loadDismissedKeys(userId: number, scope: string): Set<string> {
    // Scan localStorage for the (userId, scope) prefix rather than tracking a separate index —
    // each user has only a handful of dismissed notes, and this runs on auth/org change (for the
    // active-org scope) or at most once per profile-page org (for explicit scopes), not per read.
    const prefix = `membershine_dismissed_note_user_${userId}_${scope}_`;
    const found = new Set<string>();
    // Reads throw in Safari Private Mode and when a browser has storage blocked. Fail open — an
    // empty set keeps notes visible rather than carrying a stale scope through to the new one.
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix) && localStorage.getItem(key) === 'true') {
          found.add(key.substring(prefix.length));
        }
      }
    } catch {
      return new Set();
    }
    return found;
  }
}
